import { amber, blue, green, grey, red } from "@mui/material/colors";

const toNumberOrNull = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? value : parsed;
};

export const hasAgrupadoPor = (motor) => {
    if (!motor) return false;

    const value = motor.agrupado_por;

    return value !== null && value !== undefined && value !== '' && Number(value) !== 0;
};

// Motor "ocupado" = tem produto vinculado. Usado para impedir agrupamento.
export const isMotorOcupado = (motor) => {
    if (!motor) return false;
    return motor.status === 'ocupado' || motor.id_produto !== null && motor.id_produto !== undefined;
};

export const normalizeMotoresFromBackend = (data = []) => {
    return [...data]
        .map((motor) => ({
            ...motor,
            id: toNumberOrNull(motor.id),
            id_equipamento: toNumberOrNull(motor.id_equipamento),
            id_produto: toNumberOrNull(motor.id_produto),
            numero_motor: Number(motor.numero_motor),
            agrupado_por:
                motor.agrupado_por === null ||
                motor.agrupado_por === undefined ||
                motor.agrupado_por === '' ||
                Number(motor.agrupado_por) === 0
                    ? null
                    : Number(motor.agrupado_por),
            qtd_produto:
                motor.qtd_produto === null || motor.qtd_produto === undefined
                    ? null
                    : Number(motor.qtd_produto),
            capacidade:
                motor.capacidade === null || motor.capacidade === undefined
                    ? null
                    : Number(motor.capacidade),
            multiplicador:
                motor.multiplicador === null || motor.multiplicador === undefined
                    ? null
                    : Number(motor.multiplicador),
            numero_rotacoes:
                motor.numero_rotacoes === null || motor.numero_rotacoes === undefined
                    ? null
                    : Number(motor.numero_rotacoes),
            is_dirty: false,
        }))
        .sort((a, b) => Number(a.numero_motor) - Number(b.numero_motor));
};

export const checkIsFirstInRow = (index, cols) => index % cols === 0;

export const checkIsLastInRow = (index, cols) => index % cols === cols - 1;

export const findIdxByNumeroMotor = (motores, numeroMotor) =>
    motores.findIndex((motor) => Number(motor.numero_motor) === Number(numeroMotor));

export const findChildIdxByParentNumero = (motores, parentNumeroMotor) =>
    motores.findIndex(
        (motor) =>
            hasAgrupadoPor(motor) &&
            Number(motor.agrupado_por) === Number(parentNumeroMotor)
    );

export const isMotorInGroup = (motores, index) => {
    const motor = motores[index];

    if (!motor) return false;

    if (hasAgrupadoPor(motor)) {
        return true;
    }

    return findChildIdxByParentNumero(motores, motor.numero_motor) !== -1;
};

export const getGroupInfo = (motores, index) => {
    const motor = motores[index];

    if (!motor) {
        return { grouped: false };
    }

    if (hasAgrupadoPor(motor)) {
        const parentIdx = findIdxByNumeroMotor(motores, motor.agrupado_por);

        if (parentIdx === -1) {
            return { grouped: false };
        }

        return {
            grouped: true,
            childIdx: index,
            parentIdx,
            childMotor: motor,
            parentMotor: motores[parentIdx],
        };
    }

    const childIdx = findChildIdxByParentNumero(motores, motor.numero_motor);

    if (childIdx !== -1) {
        return {
            grouped: true,
            childIdx,
            parentIdx: index,
            childMotor: motores[childIdx],
            parentMotor: motor,
        };
    }

    return { grouped: false };
};

export const getSelectable = (index, motores, cols) => {
    const selectable = [];

    if (!checkIsFirstInRow(index, cols)) {
        const prevIdx = index - 1;
        const prev = motores[prevIdx];

        if (prev && !isMotorInGroup(motores, prevIdx)) {
            selectable.push(prevIdx);
        }
    }

    if (!checkIsLastInRow(index, cols)) {
        const nextIdx = index + 1;
        const next = motores[nextIdx];

        if (next && !isMotorInGroup(motores, nextIdx)) {
            selectable.push(nextIdx);
        }
    }

    return selectable;
};

export const computePreviewSet = (firstIdx, hoverIdx, motores, cols) => {
    if (firstIdx === null || hoverIdx === null) {
        return new Set();
    }

    if (hoverIdx === firstIdx) {
        return new Set();
    }

    if (!getSelectable(firstIdx, motores, cols).includes(hoverIdx)) {
        return new Set();
    }

    return new Set([firstIdx, hoverIdx]);
};

export const resolvePair = (idxA, idxB, motores) => {
    const a = motores[idxA];
    const b = motores[idxB];

    if (Number(a.numero_motor) < Number(b.numero_motor)) {
        return {
            firstMotorIndex: idxA,
            targetMotorIndex: idxB,
        };
    }

    return {
        firstMotorIndex: idxB,
        targetMotorIndex: idxA,
    };
};

export const computeGroupUpdate = (motores, selectedMotorIndex, targetMotorIndex) => {
    const updated = motores.map((motor) => ({ ...motor }));

    const selectedMotor = updated[selectedMotorIndex];
    const targetMotor = updated[targetMotorIndex];

    if (!selectedMotor || !targetMotor) {
        return updated;
    }

    const isGrouping = !hasAgrupadoPor(selectedMotor);

    if (isGrouping) {
        // AGRUPAR: o filho aponta pro pai. Mantém o payload esperado pelo backend.
        updated[selectedMotorIndex] = {
            ...selectedMotor,
            agrupado_por: Number(targetMotor.numero_motor),
            id_produto: null,
            qtd_produto: null,
            capacidade: targetMotor.capacidade,
            tempo_rotacao: targetMotor.tempo_rotacao,
            status: targetMotor.status,
            is_dirty: true,
        };
    } else {
        // DESAGRUPAR: reseta AMBOS os motores (filho e pai) para ficarem limpos.
        // O motor pai é identificado pelo numero_motor guardado em agrupado_por.
        const parentNumero = Number(selectedMotor.agrupado_por);
        const parentIdx = updated.findIndex(
            (m) => Number(m.numero_motor) === parentNumero
        );

        const cleanState = {
            agrupado_por: null,
            id_produto: null,
            id_produto_pai: null,
            mustSelectChild: false,
            qtd_produto: 0,
            capacidade: 1,
            multiplicador: 1,
            numero_rotacoes: 1,
            status: 'livre',
            is_dirty: true,
        };

        updated[selectedMotorIndex] = {
            ...selectedMotor,
            ...cleanState,
        };

        if (parentIdx !== -1) {
            updated[parentIdx] = {
                ...updated[parentIdx],
                ...cleanState,
                // pai nunca teve agrupado_por, mantém null de qualquer forma
                agrupado_por: null,
            };
        }
    }

    return updated;
};

export const buildCells = (motores, cols) => {
    const cells = [];
    const consumed = new Set();

    motores.forEach((currentMotor, physIdx) => {
        if (consumed.has(physIdx)) {
            return;
        }

        const group = getGroupInfo(motores, physIdx);

        if (!group.grouped) {
            const row = Math.floor(physIdx / cols);
            const col = physIdx % cols;

            consumed.add(physIdx);

            cells.push({
                motor: currentMotor,
                configMotor: currentMotor,
                physIdx,
                gridRow: row + 1,
                gridCol: col + 1,
                colSpan: 1,
                grouped: false,
            });

            return;
        }

        const { childIdx, parentIdx, childMotor, parentMotor } = group;

        const leftIdx = Math.min(childIdx, parentIdx);
        const rightIdx = Math.max(childIdx, parentIdx);

        if (physIdx !== leftIdx) {
            return;
        }

        const sameRow = Math.floor(leftIdx / cols) === Math.floor(rightIdx / cols);
        const adjacent = rightIdx - leftIdx === 1;

        if (!sameRow || !adjacent) {
            const row = Math.floor(physIdx / cols);
            const col = physIdx % cols;

            consumed.add(physIdx);

            cells.push({
                motor: currentMotor,
                configMotor: currentMotor,
                physIdx,
                gridRow: row + 1,
                gridCol: col + 1,
                colSpan: 1,
                grouped: false,
            });

            return;
        }

        consumed.add(leftIdx);
        consumed.add(rightIdx);

        const leftMotor = motores[leftIdx];
        const rightMotor = motores[rightIdx];

        const row = Math.floor(leftIdx / cols);
        const col = leftIdx % cols;

        cells.push({
            motor: leftMotor,
            partner: rightMotor,
            childMotor,
            parentMotor,
            configMotor: parentMotor,
            physIdx: leftIdx,
            partnerIdx: rightIdx,
            childIdx,
            parentIdx,
            gridRow: row + 1,
            gridCol: col + 1,
            colSpan: 2,
            grouped: true,
        });
    });

    return cells;
};

// ============================================================================
// CORES / LEGENDA — paleta.js unificada entre SecondStep, ThirdStep e Restocking
// ----------------------------------------------------------------------------
// ocupado          -> verde escuro   (green[900])
// livre            -> verde claro    (green[200])
// bloqueado        -> vermelho       (red[700])
// selecionado      -> amarelo        (amber[300])  [aplicado no componente]
// disponível agrup.-> azul claro     (blue[100])   [aplicado no componente]
// agrupado         -> mantém a cor do STATUS + borda tracejada destacada
// ============================================================================

// Borda destacada usada para sinalizar um par agrupado (mantendo a cor do status).
export const GROUP_BORDER = `3px dashed ${grey[800]}`;

export const statusColors = (status) => {
    switch (status) {
        case 'ocupado':
            return { bg: green[900], bd: green[900], hover: green[800], fg: 'white' };

        case 'bloqueado':
            return { bg: red[700], bd: red[900], hover: red[800], fg: 'white' };

        case 'inativo':
            return { bg: grey[600], bd: grey[700], hover: grey[700], fg: 'white' };

        default: // livre
            return { bg: green[200], bd: green[400], hover: green[300], fg: green[900] };
    }
};

export function getMotorColors(status) {
    switch (status) {
        case 'ocupado':
            return { bg: green[900], hoverBg: green[800], border: green[900], color: 'white' };

        case 'bloqueado':
            return { bg: red[700], hoverBg: red[800], border: red[900], color: 'white' };

        case 'inativo':
            return { bg: grey[600], hoverBg: grey[700], border: grey[700], color: 'white' };

        case 'livre':
            return { bg: green[200], hoverBg: green[600], border: green[400], color: green[900] };

        default:
            return { bg: green[900], hoverBg: green[800], border: green[900], color: 'white' };
    }
}

// Legenda completa (SecondStep — tem agrupamento).
export const LEGEND = [
    { label: 'Motor Selecionado', bg: amber[300], border: amber[600] },
    { label: 'Disponível p/ agrupar', bg: blue[100], border: blue[400] },
    { label: 'Motor Livre', bg: green[200], border: green[400] },
    { label: 'Motor Ocupado', bg: green[900], border: green[900] },
    { label: 'Motor Agrupado', bg: green[200], border: grey[800], dashed: true },
    { label: 'Motor Bloqueado', bg: red[700], border: red[900] },
];

// Legenda de status (ThirdStep / Restocking — sem seleção de agrupamento).
export const LEGEND_STATUS = [
    { label: 'Motor Selecionado', bg: amber[300], border: amber[600] },
    { label: 'Motor Livre', bg: green[200], border: green[400] },
    { label: 'Motor Ocupado', bg: green[900], border: green[900] },
    { label: 'Motor Agrupado', bg: green[200], border: grey[800], dashed: true },
    { label: 'Motor Bloqueado', bg: red[700], border: red[900] },
];